import Text from "../../../../../../../../../../components/Text";
import {
  NonNullableDate,
  formatTime,
} from "../../../../../../../../../../utils/date-time-utility";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_ICON_COLOR,
} from "../../../../../../../../../../utils/utils";

// Interface for flattened vital data
interface AllVitalsConstant {
  vitalType: string;
  value: string;
  unit: string;
  recordedDate: string;
  patientFlag?: string;
  method?: string;
  note?: string;
}

const VitalsCommonTable = ({ vitalsList }: { vitalsList: any }) => {
  // Map of vital type names to display labels
  const vitalLabels: Record<string, string> = {
    bloodPressure: "Blood Pressure",
    heartRate: "Heart Rate",
    respirationRate: "Respiration Rate",
    bloodGlucose: "Blood Glucose",
    height: "Height",
    weight: "Weight",
    bodyTemperature: "Body Temperature",
    o2Saturation: "O2 Saturation",
    bmi: "BMI",
    pulse: "Pulse",
    pulseRate: "Pulse Rate",
    peakExpiratoryFlow: "Peak Expiratory Flow",
    forceExpiratoryVolume: "Forced Expiratory Volume",
    perfusionIndex: "Perfusion Index",
    respiratoryVolume: "Respiratory Volume",
  };

  // Define vital field mappings for the second data format
  const vitalFields = [
    {
      field: "bloodGlucose",
      unitField: "bloodGlucoseUnit",
      label: "Blood Glucose",
    },
    { field: "heartRate", unitField: "heartRateUnit", label: "Heart Rate" },
    {
      field: "respirationRate",
      unitField: "respirationRateUnit",
      label: "Respiration Rate",
    },
    { field: "height", unitField: "heightUnit", label: "Height" },
    { field: "weight", unitField: "weightUnit", label: "Weight" },
    {
      field: "bodyTemperature",
      unitField: "bodyTemperatureUnit",
      label: "Body Temperature",
    },
    {
      field: "o2Saturation",
      unitField: "o2SaturationUnit",
      label: "O2 Saturation",
    },
    { field: "bmi", unitField: "bmiUnit", label: "BMI" },
    { field: "pulse", unitField: "pulseUnit", label: "Pulse" },
    {
      field: "peakExpiratoryFlow",
      unitField: "peakExpiratoryFlowUnit",
      label: "Peak Expiratory Flow",
    },
    {
      field: "forceExpiratoryVolume",
      unitField: "forceExpiratoryVolumeUnit",
      label: "Forced Expiratory Volume",
    },
    {
      field: "perfusionIndex",
      unitField: "perfusionIndexUnit",
      label: "Perfusion Index",
    },
    {
      field: "respiratoryVolume",
      unitField: "respiratoryVolumeUnit",
      label: "Respiratory Volume",
    },
  ];

  // Helper function to create vital object
  const createVital = (
    vitalType: string,
    value: string,
    unit: string,
    row: any
  ): AllVitalsConstant => ({
    vitalType,
    value,
    unit,
    recordedDate: row?.recordedDate,
    patientFlag: row?.patientFlag,
    method: row?.method,
    note: row?.note,
  });

  // Helper function to handle blood pressure
  const addBloodPressure = (row: any, label: string, unitField?: string) => {
    if (row.systolic && row.diastolic) {
      allVitals.push(
        createVital(
          label,
          `${row.systolic}/${row.diastolic}`,
          unitField ? row[unitField] || "mmHg" : row.unit || "mmHg",
          row
        )
      );
      return true;
    }
    return false;
  };

  // Process vitals from the list
  const allVitals: AllVitalsConstant[] = [];

  (vitalsList ?? []).forEach((row: any) => {
    // Format 1: Data with name and value fields at top level
    if (row.name && row.value) {
      const label = vitalLabels[row.name] || row.name;

      if (row.name === "bloodPressure") {
        addBloodPressure(row, label);
      } else {
        allVitals.push(createVital(label, row.value, row.unit || "", row));
      }
      return;
    }

    // Format 2: Data with specific field names (bloodGlucose, heartRate, etc.)
    // Handle blood pressure
    addBloodPressure(row, "Blood Pressure", "bloodPressureUnit");

    // Handle all other vitals
    vitalFields.forEach(({ field, unitField, label }) => {
      if (row[field]) {
        allVitals.push(
          createVital(label, row[field], row[unitField] || "", row)
        );
      }
    });
  });

  return (
    <>
      {!!allVitals.length && (
        <>
          <Text
            fontSize={DEFAULT_FONTSIZE.fontSize}
            fontWeight={500}
            ml={1}
            mb={1}
          >
            Vitals
          </Text>
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    fontSize: DEFAULT_FONTSIZE.fontSize,
                    color: DEFAULT_ICON_COLOR,
                  }}
                >
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Recorded At
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Label
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {allVitals.map((vital, idx) => (
                  <tr
                    key={`${vital.vitalType}-${idx}`}
                    style={{ fontSize: DEFAULT_FONTSIZE.fontSize }}
                  >
                    <td
                      style={{ border: "1px solid #ddd", padding: "4px 8px" }}
                    >
                      {vital.recordedDate
                        ? `${NonNullableDate(
                            new Date(vital.recordedDate)
                          )}, ${formatTime(new Date(vital.recordedDate))}`
                        : "-"}
                    </td>
                    <td
                      style={{ border: "1px solid #ddd", padding: "4px 8px" }}
                    >
                      <Text fontWeight={500}>{vital.vitalType}</Text>
                    </td>
                    <td
                      style={{ border: "1px solid #ddd", padding: "4px 8px" }}
                    >
                      <Text fontWeight={600} display="inline">
                        {vital.value}
                        {vital.unit && (
                          <Text
                            display="inline"
                            fontSize="0.85em"
                            fontWeight={400}
                            ml={1}
                          >
                            {vital.unit}
                          </Text>
                        )}
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default VitalsCommonTable;
